/**
 * Path-mode load case rendering.
 *
 * Each loadcase from the section-info response carries a CZML payload with:
 *  - line entities (id: `<loadcase> line:<numericId>`)
 *  - LineTerrain polylines (id: `LineTerrain:<loadcase> line:<numericId>`)
 *  - LineTerrainLabel entities (id: `LineTerrainLabel:<loadcase> line:<numericId>`)
 *  - (TerrainProfile is currently excluded server-side; not handled here.)
 *
 * Each loadcase is rendered into two CzmlDataSources (lines + terrain) so
 * "Vis avstander" can flip terrain visibility independently of the line
 * datasource. Originals (plan/live `line:<id>` entities) are hidden whenever
 * any active loadcase covers them and restored when no active loadcase does.
 *
 * When several loadcases are active, only the loadcase with the *shortest*
 * distance for each line shows its LineTerrain — overlapping vertical
 * measurements would be visually noisy.
 *
 * Visibility recomputes whenever `pathMode.activeLoadCases` or
 * `pathMode.showDistances` changes in the store.
 */

import {
	Cartesian3,
	Cartographic,
	CzmlDataSource,
	JulianDate,
	sampleTerrainMostDetailed,
	type Entity
} from 'cesium';
import { get } from 'svelte/store';

import { useCesiumViewer } from '../cesium-viewer';
import { pathModeState } from './path-mode-state.svelte';
import { useDataSources } from '../cesium-datasources';
import { mapModeStore } from '../map-mode-store';
import { type ILoadCase } from '../map-tools-store';

type TerrainEntry = {
	lineTerrain?: Entity;
	label?: Entity;
	/**
	 * Lowest conductor-to-ground clearance for this line in metres. Sourced
	 * from the calculation response (`lowestDistanceLineTerrain`) — the
	 * authoritative value also shown in the hover tooltip and report — and only
	 * falls back to the LineTerrain polyline geometry when the server didn't
	 * supply it (e.g. the stiff `/info` pass). Drives the per-line winner pick.
	 */
	distance: number;
};

type LoadCaseRendering = {
	name: string;
	lineDataSource: CzmlDataSource;
	terrainDataSource: CzmlDataSource;
	/** Underlying line ids (e.g. `line:2242746`) referenced by this loadcase. */
	coveredLineIds: Set<string>;
	/**
	 * Direct lookup keyed by the underlying line id (e.g. `line:2242746`).
	 * Built from the loaded entity references rather than relying on
	 * `entities.getById(<concatenated id>)`, which can miss CZML-loaded
	 * entities whose ids contain spaces.
	 */
	lineEntitiesByUnderlyingId: Map<string, Entity>;
	terrainByLineId: Map<string, TerrainEntry>;
};

type OriginalLineEntry = {
	entity: Entity;
	originalShow: boolean;
};

// ── Module-local state ──────────────────────────────────────────────────────

const renderings = new Map<string, LoadCaseRendering>();
const originalLineEntities = new Map<string, OriginalLineEntry>();

let lastActiveKey = '';
let lastShowDistances = false;

// ── Reactive watcher ────────────────────────────────────────────────────────
// Recompute visibility whenever activeLoadCases or showDistances change.
// `$effect.root` creates a module-lifetime effect tree; the inner `$effect`
// then tracks reads of `pathModeState.*` properties via Svelte 5 runes
// (replaces the previous `mapToolsStore.subscribe` pattern).

$effect.root(() => {
	$effect(() => {
		const active = pathModeState.activeLoadCases;
		const showDistances = pathModeState.showDistances;
		if (renderings.size === 0) return;
		const activeKey = [...active].sort().join('|');
		if (activeKey === lastActiveKey && showDistances === lastShowDistances) return;
		lastActiveKey = activeKey;
		lastShowDistances = showDistances;
		applyVisibility(active, showDistances);
	});
});

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Rebuilds every load-case datasource from scratch and applies terrain/line
 * visibility for `activeNames`.
 *
 * `activeNames` defaults to the current `pathModeState.activeLoadCases`, but
 * the /info and /calculate flows pass the *final* set explicitly. They assign
 * `pathModeState.activeLoadCases` only *after* this call (so the styling effect
 * sees the rebuilt line entities), which means the visibility here would
 * otherwise run against the stale previous selection and rely on the reactive
 * watcher re-firing from that later assignment. Module-scope `$state` writes
 * (after an `await`) don't reliably flush that effect before the map is shown,
 * so we apply the correct selection directly and keep the watcher's dedupe
 * baseline in sync below.
 */
export async function applyLoadCases(
	loadCases: ILoadCase[],
	activeNames: string[] = pathModeState.activeLoadCases
): Promise<void> {
	const viewer = useCesiumViewer();
	if (!viewer) return;

	clearLoadCases();

	for (const lc of loadCases) {
		const rendering = await buildLoadCaseRendering(lc);
		if (!rendering) continue;
		viewer.dataSources.add(rendering.lineDataSource);
		viewer.dataSources.add(rendering.terrainDataSource);
		renderings.set(rendering.name, rendering);
	}

	hideMatchingOriginals();

	applyVisibility(activeNames, pathModeState.showDistances);
	// Sync the watcher's dedupe baseline to what we just applied so the upcoming
	// `activeLoadCases = activeNames` assignment is correctly treated as a no-op
	// rather than re-applying (or being skipped against a stale baseline).
	lastActiveKey = [...activeNames].sort().join('|');
	lastShowDistances = pathModeState.showDistances;
}

export function clearLoadCases(): void {
	const viewer = useCesiumViewer();
	if (viewer) {
		for (const r of renderings.values()) {
			viewer.dataSources.remove(r.lineDataSource, true);
			viewer.dataSources.remove(r.terrainDataSource, true);
		}
	}
	renderings.clear();

	for (const original of originalLineEntities.values()) {
		original.entity.show = original.originalShow;
	}
	originalLineEntities.clear();

	lastActiveKey = '';
	lastShowDistances = false;
}

// ── Internal: build one rendering ───────────────────────────────────────────

async function buildLoadCaseRendering(lc: ILoadCase): Promise<LoadCaseRendering | null> {
	const documentPacket = lc.czml.find((p) => p?.id === 'document') ?? {
		id: 'document',
		version: '1.0'
	};

	const linePackets = lc.czml.filter(
		(p) => typeof p?.id === 'string' && p.id.startsWith(`${lc.name} `)
	);
	const terrainPackets = lc.czml.filter(
		(p) =>
			typeof p?.id === 'string' &&
			(p.id.startsWith('LineTerrain:') || p.id.startsWith('LineTerrainLabel:'))
	);

	if (linePackets.length === 0 && terrainPackets.length === 0) return null;

	const lineDataSource = new CzmlDataSource(`path-tool-loadcase-lines:${lc.name}`);
	await lineDataSource.load([documentPacket, ...linePackets]);

	const terrainDataSource = new CzmlDataSource(`path-tool-loadcase-terrain:${lc.name}`);
	await terrainDataSource.load([documentPacket, ...terrainPackets]);

	// Path-mode keeps lines / LineTerrain / labels visible at any zoom.
	disableDistanceDisplayCondition(lineDataSource.entities.values);
	disableDistanceDisplayCondition(terrainDataSource.entities.values);

	const coveredLineIds = new Set<string>();
	const lineEntitiesByUnderlyingId = new Map<string, Entity>();
	const linePrefix = `${lc.name} `;
	for (const entity of lineDataSource.entities.values) {
		if (!entity.id.startsWith(linePrefix)) continue;
		const underlying = entity.id.substring(linePrefix.length);
		coveredLineIds.add(underlying);
		lineEntitiesByUnderlyingId.set(underlying, entity);
	}

	// Authoritative per-line lowest clearance from the calculation response,
	// keyed (`line:<numericId>`) to match the terrain entries below. Preferred
	// over the polyline geometry, which only spans the LineTerrain's first two
	// vertices and so doesn't reliably represent the line's lowest clearance.
	const serverDistanceByLineId = new Map<string, number>();
	for (const lineCalc of lc.calculations?.lines ?? []) {
		if (typeof lineCalc.lowestDistanceLineTerrain === 'number') {
			serverDistanceByLineId.set(`line:${lineCalc.id}`, lineCalc.lowestDistanceLineTerrain);
		}
	}

	const terrainByLineId = new Map<string, TerrainEntry>();
	const time = JulianDate.now();

	for (const entity of terrainDataSource.entities.values) {
		const parsed = parseTerrainEntityId(entity.id, lc.name);
		if (!parsed) continue;
		const { lineId, kind } = parsed;

		let entry = terrainByLineId.get(lineId);
		if (!entry) {
			entry = { distance: 0 };
			terrainByLineId.set(lineId, entry);
		}
		entry[kind] = entity;

		if (kind === 'lineTerrain') {
			const serverDistance = serverDistanceByLineId.get(lineId);
			if (serverDistance !== undefined) {
				entry.distance = serverDistance;
			} else if (entity.polyline?.positions) {
				const positions = entity.polyline.positions.getValue(time) as Cartesian3[] | undefined;
				if (positions && positions.length >= 2) {
					entry.distance = Cartesian3.distance(positions[0], positions[1]);
				}
			}
		}
	}

	// ── DIAGNOSTIC: raw per-load-case LineTerrain geometry as received ───────
	// Logs exactly what the server put in THIS load case's CZML, before any
	// winner/visibility logic. If two different load cases print identical
	// top/bottom/delta for the same lineId, the server is sending the same
	// LineTerrain geometry in every load case's CZML (i.e. not recomputed per
	// load case) — proving the discrepancy is server-side, not a client mix-up.
	{
		const heightOf = (c: Cartesian3) => Cartographic.fromCartesian(c).height;
		const rawRows: Record<string, unknown>[] = [];
		for (const entity of terrainDataSource.entities.values) {
			if (!entity.polyline?.positions) continue;
			const pos = entity.polyline.positions.getValue(time) as Cartesian3[] | undefined;
			if (!pos || pos.length === 0) continue;
			const h = pos.map(heightOf);
			rawRows.push({
				rawId: entity.id,
				pts: pos.length,
				top: Number(Math.max(...h).toFixed(2)),
				bottom: Number(Math.min(...h).toFixed(2)),
				delta: Number((Math.max(...h) - Math.min(...h)).toFixed(2))
			});
    }
		// Logging loadcase data for each line and loadcase
		// eslint-disable-next-line no-console
		//console.log(`[terrain-raw] loadCase="${lc.name}" terrainPackets=${terrainPackets.length}`);
		// eslint-disable-next-line no-console
		//console.table(rawRows);
	}

	return {
		name: lc.name,
		lineDataSource,
		terrainDataSource,
		coveredLineIds,
		lineEntitiesByUnderlyingId,
		terrainByLineId
	};
}

/**
 * LineTerrain / LineTerrainLabel ids may arrive in two shapes:
 *  - new: `LineTerrain:<loadcase> line:<numericId>` (server is migrating to this)
 *  - old: `LineTerrain:<numericId>` (current responses)
 * Either way we resolve to the underlying line entity id `line:<numericId>`.
 */
function parseTerrainEntityId(
	entityId: string,
	loadCaseName: string
): { lineId: string; kind: 'lineTerrain' | 'label' } | null {
	const lineTerrainNew = `LineTerrain:${loadCaseName} `;
	if (entityId.startsWith(lineTerrainNew)) {
		return { lineId: entityId.substring(lineTerrainNew.length), kind: 'lineTerrain' };
	}
	const labelNew = `LineTerrainLabel:${loadCaseName} `;
	if (entityId.startsWith(labelNew)) {
		return { lineId: entityId.substring(labelNew.length), kind: 'label' };
	}

	const lineTerrainOld = 'LineTerrain:';
	if (entityId.startsWith(lineTerrainOld)) {
		const suffix = entityId.substring(lineTerrainOld.length);
		// Skip new-format strings that happen to share the prefix but use a
		// different loadcase name (those contain a space).
		if (!suffix.includes(' ')) return { lineId: `line:${suffix}`, kind: 'lineTerrain' };
	}
	const labelOld = 'LineTerrainLabel:';
	if (entityId.startsWith(labelOld)) {
		const suffix = entityId.substring(labelOld.length);
		if (!suffix.includes(' ')) return { lineId: `line:${suffix}`, kind: 'label' };
	}

	return null;
}

/**
 * All loadcase line entities (across all loadcases) that mirror the given
 * underlying plan/live line id (e.g. `line:2242746`). Used by callers that
 * need to apply per-line styling consistently regardless of which datasource
 * is currently visible.
 */
export function getLoadCaseLineEntities(underlyingLineId: string): Entity[] {
	const result: Entity[] = [];
	for (const r of renderings.values()) {
		const entity = r.lineEntitiesByUnderlyingId.get(underlyingLineId);
		if (entity) result.push(entity);
	}
	return result;
}

/**
 * All line entities owned by a single load case rendering. Used by
 * `applyLoadCaseLineColors` to paint each load case's duplicates with its
 * own rgb when the panel is in "loadcase" colour mode.
 */
export function getLoadCaseLineEntitiesByLoadCaseName(loadCaseName: string): Entity[] {
	const r = renderings.get(loadCaseName);
	if (!r) return [];
	return [...r.lineEntitiesByUnderlyingId.values()];
}

// ── Internal: hide / restore originals ──────────────────────────────────────

function hideMatchingOriginals(): void {
	const dataSources = collectMapDataSources();

	const allCovered = new Set<string>();
	for (const r of renderings.values()) {
		for (const id of r.coveredLineIds) allCovered.add(id);
	}

	for (const lineId of allCovered) {
		if (originalLineEntities.has(lineId)) continue;
		for (const ds of dataSources) {
			const entity = ds.entities.getById(lineId);
			if (entity) {
				originalLineEntities.set(lineId, {
					entity,
					originalShow: entity.show ?? true
				});
				break;
			}
		}
	}
}

function collectMapDataSources() {
	const { mapdataDataSource } = useDataSources();
	const planDataSource = get(mapModeStore).activeDataSource;
	const list = [];
	if (mapdataDataSource) list.push(mapdataDataSource);
	if (planDataSource) list.push(planDataSource);
	return list as { entities: { getById: (id: string) => Entity | undefined } }[];
}

// ── Internal: visibility application ────────────────────────────────────────

function applyVisibility(activeNames: string[], showDistances: boolean): void {
	const active = new Set(activeNames);

	// 1. Loadcase line datasources: shown when their loadcase is active.
	for (const [name, r] of renderings) {
		r.lineDataSource.show = active.has(name);
	}

	// 2. Pick the active loadcase with the shortest distance per line.
	const winnerByLineId = new Map<string, string>();
	for (const name of active) {
		const r = renderings.get(name);
		if (!r) continue;
		for (const [lineId, entry] of r.terrainByLineId) {
			const current = winnerByLineId.get(lineId);
			if (!current) {
				winnerByLineId.set(lineId, name);
				continue;
			}
			const currentDist =
				renderings.get(current)?.terrainByLineId.get(lineId)?.distance ?? Infinity;
			if (entry.distance < currentDist) winnerByLineId.set(lineId, name);
		}
	}

	// 3. Apply per-entity show in terrain datasources.
	for (const [name, r] of renderings) {
		const isActive = active.has(name);
		for (const [lineId, entry] of r.terrainByLineId) {
			const visible = showDistances && isActive && winnerByLineId.get(lineId) === name;
			if (entry.lineTerrain) entry.lineTerrain.show = visible;
			if (entry.label) entry.label.show = visible;
		}
	}

	// 4. Hide originals covered by at least one active loadcase, otherwise
	//    restore. Lines that no loadcase ever covered are left untouched.
	for (const [lineId, original] of originalLineEntities) {
		const covered = activeCoversLine(active, lineId);
		original.entity.show = covered ? false : original.originalShow;
	}
}

function activeCoversLine(active: Set<string>, lineId: string): boolean {
	for (const name of active) {
		if (renderings.get(name)?.coveredLineIds.has(lineId)) return true;
	}
	return false;
}

/**
 * Strip the per-graphics `distanceDisplayCondition` so the given entities stay
 * visible at any camera distance. Path-mode measurements (load-case lines,
 * LineTerrain + labels, terrain profiles + offsets) should never be culled by
 * zoom the way the global distance view's entities are — path-mode is an
 * up-close *and* zoomed-out inspection mode.
 */
export function disableDistanceDisplayCondition(entities: Entity[]): void {
	for (const e of entities) {
		if (e.polyline) e.polyline.distanceDisplayCondition = undefined;
		if (e.label) e.label.distanceDisplayCondition = undefined;
		if (e.point) e.point.distanceDisplayCondition = undefined;
		if (e.billboard) e.billboard.distanceDisplayCondition = undefined;
	}
}
